import { useMemo, type ReactNode } from 'react';
import {
  SceneCapabilityProvider,
  SceneProgressProvider,
  type SceneCapability,
} from '@ircf/three/runtime';
import { useCapability } from './capability.js';

/**
 * Connects the application's capability detection to the 3D package.
 *
 * `@ircf/three` deliberately probes nothing itself - the app already decides once
 * whether WebGL is available, which quality tier applies and whether the visitor
 * has asked for reduced motion, and the portal accents depend on the same
 * decision. Two independent detectors would drift apart. This is the single place
 * that decision crosses the package boundary.
 *
 * Import from `@ircf/three/runtime`, not the package barrel: the barrel also
 * exports the canvas and scenes, and a static import of those would put the
 * Three.js chunk on every authenticated route.
 *
 * Both providers are mounted for the whole application rather than per page:
 * the progress provider watches the document for `[data-scene-chapter]` elements
 * and finds none on routes that have no scene, which costs one query per
 * navigation and keeps the public pages free of any 3D wiring of their own.
 */
export function SceneBridge({ children }: { children: ReactNode }): JSX.Element {
  const capability = useCapability();

  const value = useMemo<SceneCapability>(
    () => ({
      enabled: capability.hasWebGl && capability.tier !== 'off',
      tier: capability.tier === 'off' ? 'low' : capability.tier,
      prefersReducedMotion: capability.prefersReducedMotion,
      isTouch: capability.isTouch,
      // The reduced-detail model is chosen by what the device can draw, not by
      // whether it has a touchscreen: a narrow window on a fast desktop should
      // still get the light asset, and a high-tier tablet should not be punished
      // for its input method.
      preferMobileAssets: capability.isNarrow || capability.isTouch || capability.tier === 'low',
      pixelRatio: capability.pixelRatio,
      reportPoorPerformance: capability.reportPoorPerformance,
    }),
    [
      capability.hasWebGl,
      capability.tier,
      capability.prefersReducedMotion,
      capability.isTouch,
      capability.isNarrow,
      capability.pixelRatio,
      capability.reportPoorPerformance,
    ],
  );

  return (
    <SceneCapabilityProvider value={value}>
      {/* Pointer tracking drives the desktop parallax. Switched off entirely
          rather than ignored downstream, so no listener is attached at all for a
          visitor who has asked for reduced motion. */}
      <SceneProgressProvider trackPointer={!capability.prefersReducedMotion && !capability.isTouch}>
        {children}
      </SceneProgressProvider>
    </SceneCapabilityProvider>
  );
}
